import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CanceledAtom, CancellationAtom, useSignature } from '../lib/state.ts';
import Header from '../Header/Header.tsx';
import { cancelReservationFinish } from '../../../lib/api.ts';
import { ApiError } from '../../../lib/ApiError.ts';
import type { WidgetCancelChoice, WidgetCancelChoiceInfo } from '../../../lib/types.ts';
import AppDownloadBanner from '../AppDownloadBanner/AppDownloadBanner.tsx';

import styles from './Confirm.module.css';

// Overview of Dorsia (cities, top restaurants) on the marketing site.
const DISCOVER_URL = 'https://dorsia.com/discover';

/**
 * Only card charges are refundable. When the booking also used booking credits
 * or Fun Coupons, say that those go back to the wallet instead; null otherwise.
 */
function walletTenderNote(choice: WidgetCancelChoiceInfo): string | null {
  const tenders = [
    choice.used_credits ? 'Booking credits' : null,
    choice.used_fun_coupons ? 'Fun Coupons' : null,
  ].filter((tender): tender is string => tender !== null);

  if (tenders.length === 0) {
    return null;
  }

  return `${tenders.join(' and ')} you used can’t be refunded to your card. They go back to your Dorsia wallet instead.`;
}

const Confirm: React.FC = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [canceling, setCanceling] = useState<boolean>(false);
  const cancellationState = useAtomValue(CancellationAtom);
  const cancellation = cancellationState.status === 'loaded' ? cancellationState.data : null;
  const setCancelFinish = useSetAtom(CanceledAtom);
  const signature = useSignature(uuid!);

  const onNext = (choice: WidgetCancelChoice = 'standard') => {
    if (!uuid || !cancellation || !signature) {
      return;
    }

    const go = async () => {
      setCanceling(true);

      try {
        const response = await cancelReservationFinish(signature, uuid, {
          type: cancellation.type,
          choice,
        });

        if (response) {
          setCancelFinish(response);
          navigate(`../canceled/`);
        }
      } catch (e: unknown) {
        if (e instanceof ApiError && e.type === 'AlreadyCanceledReservationErrorResponse') {
          navigate(`../canceled/`);
        } else if (e instanceof Error) {
          console.error('ERROR', e.message);
          Sentry.captureException(e);
        }
      } finally {
        setCanceling(false);
      }
    };

    void go();
  };

  if (!cancellation) {
    // Only show the not-found message once the fetch has actually failed;
    // while it's in flight, show a neutral loading state instead of flashing an error.
    // (useCancellation only reports 'loaded' when the response has a reservation.)
    return (
      <div>
        <Header
          title={
            cancellationState.status === 'error' ? 'Sorry, cannot find reservation' : 'Loading...'
          }
        />
      </div>
    );
  }

  const reservation_at = dayjs(cancellation.reservation.reservation_at);
  const isEvent = cancellation.reservation.restaurant?.type === 'event';
  // Events are prepaid tickets: prefer the processed total, fall back to the
  // prepayment amount for future reservations whose transactions haven't settled.
  const eventTotal =
    cancellation.reservation.total_paid > 0
      ? cancellation.reservation.total_paid__currency
      : cancellation.reservation.total_prepayment_amount > 0
        ? cancellation.reservation.total_prepayment_amount__currency
        : null;

  const choice = cancellation.widget_cancel_choice;
  const feePercent = choice?.fee_percent ?? 5;
  const walletNote = choice ? walletTenderNote(choice) : null;
  const hasWalletReturn =
    !!choice && (choice.refund_credits_back > 0 || choice.refund_fun_coupons_back > 0);

  return (
    <>
      <Header title="Are you sure you would like to cancel your reservation?" />

      <div className="content">
        <h2 className={styles.venue}>{cancellation.reservation.restaurant?.name}</h2>

        <div className={clsx('section', styles.details)}>
          <div>
            {reservation_at.tz(cancellation.reservation.restaurant.timezone).format('ddd, MMMM D')}
          </div>
          <div>{cancellation.reservation.reservation_time_pretty}</div>
          <div>{cancellation.reservation.party_size} guests</div>
          <div>{cancellation.reservation.table_type}</div>

          {/* Events are prepaid tickets, not minimum-spend tables, so show what was paid.
              Restaurants keep the minimums, hidden when not configured. */}
          {isEvent ? (
            eventTotal && <div className={styles.minimum}>{eventTotal} total</div>
          ) : (
            <>
              {cancellation.reservation.table_minimum > 0 && (
                <div className={styles.minimum}>
                  {cancellation.reservation.table_minimum__currency} total minimum
                </div>
              )}
              {cancellation.reservation.price_per_person > 0 && (
                <div className={styles.minimum_pp}>
                  {cancellation.reservation.price_per_person__currency} minimum per person
                </div>
              )}
            </>
          )}
        </div>

        {choice && choice.eligible ? (
          <div className={styles.choice}>
            {cancellation.reservation.cancellation_policy && (
              <div className={styles.policyNote}>
                <span className={styles.eyebrow}>Cancellation policy</span>
                <p>{cancellation.reservation.cancellation_policy}</p>
              </div>
            )}

            <span className={styles.eyebrow}>How would you like to cancel?</span>

            <section className={clsx(styles.optionCard, styles.optionCardFeatured)}>
              <span className={styles.badge}>Recommended</span>
              <h3 className={styles.optionTitle}>Cancel &amp; receive booking credits</h3>

              <ul className={styles.benefits}>
                <li>
                  {choice.credits_back > 0
                    ? `Full ${choice.credits_back__currency} back as booking credits`
                    : 'Your full amount back as booking credits'}{' '}
                  &mdash; they never expire
                </li>
                <li>30 days of Dorsia access, complimentary ($200/yr value)</li>
                <li>Use them at any restaurant or event on Dorsia</li>
              </ul>

              <button
                className="nextButton"
                onClick={() => onNext('gift')}
                disabled={canceling}
                type="button"
              >
                Cancel &amp; receive credits
              </button>

              {/* Widget guests often haven't heard of Dorsia; this explains the
                  credits-and-access option and links to the overview page. */}
              <aside className={styles.about}>
                <span className={styles.eyebrow}>What is Dorsia?</span>
                <p>
                  Dorsia is the members-only app for the hardest reservations in New York, Los
                  Angeles, Miami, London and beyond.
                </p>
                <a
                  className={styles.aboutLink}
                  href={DISCOVER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more &rarr;
                </a>
              </aside>

              <AppDownloadBanner
                className={styles.cardFooter}
                label="Dorsia app"
                text="Download Dorsia to redeem your credits and access"
              />
            </section>

            <section className={styles.optionCard}>
              <h3 className={styles.optionTitle}>Cancel &amp; refund to card</h3>
              <p className={styles.optionBody}>
                Refunds aren&rsquo;t normally offered. For now, you can refund to your card less a{' '}
                {feePercent}% processing fee.
                {walletNote && <> {walletNote}</>}
              </p>

              <dl className={styles.breakdown}>
                <div className={styles.breakdownRow}>
                  <dt>Charged to card</dt>
                  <dd>{choice.card_charge_total__currency}</dd>
                </div>
                {choice.cancellation_fee > 0 && (
                  <div className={styles.breakdownRow}>
                    <dt>Cancellation fee ({choice.cancellation_fee_percent}%)</dt>
                    <dd>&minus;{choice.cancellation_fee__currency}</dd>
                  </div>
                )}
                <div className={styles.breakdownRow}>
                  <dt>Processing fee ({feePercent}%)</dt>
                  <dd>&minus;{choice.card_refund_fee__currency}</dd>
                </div>
                <div className={clsx(styles.breakdownRow, styles.breakdownTotal)}>
                  <dt>Refund to card</dt>
                  <dd>{choice.card_refund_after_fee__currency}</dd>
                </div>
                {hasWalletReturn && (
                  <div className={styles.breakdownWallet}>
                    {choice.refund_credits_back > 0 && (
                      <div className={styles.breakdownRow}>
                        <dt>Booking credits back to your Dorsia wallet</dt>
                        <dd>{choice.refund_credits_back__currency}</dd>
                      </div>
                    )}
                    {choice.refund_fun_coupons_back > 0 && (
                      <div className={styles.breakdownRow}>
                        <dt>Fun Coupons back to your Dorsia wallet</dt>
                        <dd>{choice.refund_fun_coupons_back__currency}</dd>
                      </div>
                    )}
                  </div>
                )}
              </dl>

              <button
                className={styles.secondaryButton}
                onClick={() => onNext('refund')}
                disabled={canceling}
                type="button"
              >
                Refund ({feePercent}% fee)
              </button>
            </section>
          </div>
        ) : (
          <div className={clsx('section', styles.policy)}>
            <h3>Cancellation policy</h3>

            {cancellation.reservation.cancellation_policy && (
              <p>{cancellation.reservation.cancellation_policy}</p>
            )}

            <button
              className="nextButton"
              onClick={() => onNext()}
              disabled={canceling}
              type="button"
            >
              Yes, I would like to cancel
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Confirm;
